import registry from "../article-registry.json";
import { authoredPairs } from "../article-discovery";
import { localePrefix, locales, type Locale } from "./config";
import { internalSegments, localizeSegments } from "./slugs";

/**
 * Routes are written once, as the Spanish (canonical, unprefixed) path, and translated with
 * localePath():
 *
 *   /                      → /en, /pt
 *   /research              → /en/articles, /pt/articles     (the developer library)
 *   /articles/<es-slug>    → /<lang>/articles/<slug>        (article-registry.json pairs)
 *   /articles/<legacy>     → /en/articles/<legacy>          (the three editorial notes keep their slug)
 *   anything else          → /<lang> + path
 *
 * Spanish and Portuguese URLs then use their own words (slugs.ts): /leaderboards/ai → /rankings/ia,
 * /pt/rankings/ia. Code keeps writing the internal segments; canonicalPath() accepts either form.
 *
 * Not every page exists in every language: a guide without a Portuguese edition is not linked or
 * advertised in Portuguese (availableLocales), and localePath() falls back to that language's
 * library instead of inventing a URL. Query strings and fragments are preserved.
 */
type Pair = Partial<Record<Locale, string>> & { es: string };
const pairs: Pair[] = [...registry.pairs, ...authoredPairs];
/** Editorial notes that predate the guide library; their English edition keeps the same slug. */
export const legacyArticleSlugs = ["aws-cost-visibility", "terraform-vs-platform-engineering", "cloudops-tco"] as const;
/** Pages that exist only in some languages (Spanish is implied). Everything else exists in all. */
const PARTIAL_PAGES: { test: (path: string) => boolean; locales: Locale[] }[] = [
  { test: p => p.startsWith("/content-published"), locales: ["es"] },
  { test: p => (legacyArticleSlugs as readonly string[]).some(s => p === `/articles/${s}`), locales: ["es", "en"] },
];

const split = (href: string) => {
  const match = href.match(/^([^?#]*)(.*)$/)!;
  return { path: match[1] || "/", rest: match[2] };
};

/** Language of a pathname: /en… is English, /pt… Portuguese, everything else Spanish. */
export function pathLocale(pathname: string | null | undefined): Locale {
  for (const locale of locales) {
    const prefix = localePrefix[locale];
    if (prefix && (pathname === prefix || pathname?.startsWith(`${prefix}/`))) return locale;
  }
  return "es";
}

/** A path without its language prefix, for matching navigation state (still in that language). */
export function unprefixedPath(pathname: string) {
  const prefix = localePrefix[pathLocale(pathname)];
  return prefix ? pathname.slice(prefix.length) || "/" : pathname;
}

const toSpanish = (path: string) => {
  const locale = pathLocale(path);
  if (locale === "es") return internalSegments("es", path);
  const bare = internalSegments(locale, unprefixedPath(path));
  if (bare === "/articles") return "/research";
  const article = bare.match(/^\/articles\/([^/]+)$/);
  if (article) {
    const pair = pairs.find(p => p[locale] === article[1]);
    return `/articles/${pair ? pair.es : article[1]}`;
  }
  return bare;
};

const internalFromSpanish = (locale: Locale, path: string) => {
  if (locale === "es") return path;
  const prefix = localePrefix[locale];
  if (path === "/") return prefix;
  if (path === "/research") return `${prefix}/articles`;
  const article = path.match(/^\/articles\/([^/]+)$/);
  if (article) {
    const slug = article[1];
    const translated = pairs.find(p => p.es === slug)?.[locale];
    if (translated) return `${prefix}/articles/${translated}`;
    if (locale === "en" && (legacyArticleSlugs as readonly string[]).includes(slug)) return `${prefix}/articles/${slug}`;
    return `${prefix}/articles`;
  }
  return `${prefix}${path}`;
};

/** The public URL of an internal Spanish path in `locale`, with that language's words. */
const fromSpanish = (locale: Locale, path: string) => {
  const internal = internalFromSpanish(locale, path);
  const prefix = localePrefix[locale];
  if (!prefix) return localizeSegments(locale, internal);
  return internal === prefix ? internal : `${prefix}${localizeSegments(locale, internal.slice(prefix.length))}`;
};

/** Spanish (canonical) form of any site path, whichever language it is written in. */
export function canonicalPath(href: string) {
  const { path, rest } = split(href);
  return `${toSpanish(path)}${rest}`;
}

/** The languages a page exists in, given its path in any language. */
export function availableLocales(href: string): Locale[] {
  const path = split(canonicalPath(href)).path;
  const article = path.match(/^\/articles\/([^/]+)$/);
  if (article) {
    const slug = article[1];
    const pair = pairs.find(p => p.es === slug);
    if (pair) return locales.filter(l => l === "es" || !!pair[l]);
  }
  const partial = PARTIAL_PAGES.find(p => p.test(path));
  return partial ? locales.filter(l => partial.locales.includes(l)) : [...locales];
}

/** The same page in `locale` (or, when it doesn't exist there, that language's nearest page).
 * Accepts a path in any language; external URLs pass through. */
export function localePath(locale: Locale, href: string) {
  if (!href.startsWith("/") || href.startsWith("//")) return href;
  const { path, rest } = split(canonicalPath(href));
  if (!availableLocales(path).includes(locale)) return locale === "es" ? path : fromSpanish(locale, path.startsWith("/articles/") ? "/research" : "/");
  // "/en#newsletter" rather than "/en/#newsletter".
  return `${fromSpanish(locale, path)}${rest}`;
}

/** Every edition of a page that exists, keyed by language. */
export function localeAlternates(href: string): Partial<Record<Locale, string>> {
  return Object.fromEntries(availableLocales(href).map(l => [l, localePath(l, href)]));
}
